import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import { titleToHtml, titleToText } from 'src/_helpers/titleMarkup'

export const CITATION_STYLES = ['apa', 'mla', 'chicago', 'harvard', 'vancouver', 'ama']

const isPolish = (locale) =>
  String(locale || '')
    .toLowerCase()
    .startsWith('pl')

const clean = (value) => {
  if (value === null || value === undefined) return ''

  return String(value).trim()
}

const join = (parts, separator = ' ') => parts.map(clean).filter(Boolean).join(separator)

const endsWithPunctuation = (value) => /[.!?]$/.test(value.replace(/<\/?[a-z]+>$/i, ''))

const terminate = (value) => {
  const text = clean(value)

  if (!text) return ''

  return endsWithPunctuation(text) ? text : `${text}.`
}

const italic = (value) => {
  const text = clean(value)

  return text ? `<i>${text}</i>` : ''
}

const getNameParts = (contributor) => {
  const firstName = contributor?.firstName?.trim() || ''
  const lastName = contributor?.lastName?.trim() || ''

  if (firstName || lastName) {
    return { firstName, lastName }
  }

  const fullNameParts = (contributor?.fullName || '').trim().split(/\s+/).filter(Boolean)

  return {
    firstName: fullNameParts.slice(0, -1).join(' '),
    lastName: fullNameParts.at(-1) || '',
  }
}

const getInitials = (firstName, separator = ' ') =>
  firstName
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((name) => `${name.charAt(0).toUpperCase()}.`)
    .join(separator)

const formatInvertedName = (contributor) => {
  const { firstName, lastName } = getNameParts(contributor)

  return join([lastName, getInitials(firstName)], ', ')
}

export const formatInvertedFullName = (contributor) => {
  const { firstName, lastName } = getNameParts(contributor)

  return join([lastName, firstName], ', ')
}

const formatInitialsFirstName = (contributor) => {
  const { firstName, lastName } = getNameParts(contributor)

  return join([getInitials(firstName), lastName], ' ')
}

const formatVancouverName = (contributor) => {
  const { firstName, lastName } = getNameParts(contributor)

  return join([lastName, getInitials(firstName, '').replaceAll('.', '')], ' ')
}

const formatFullName = (contributor) => {
  const { firstName, lastName } = getNameParts(contributor)

  return join([firstName, lastName], ' ')
}

const formatNames = (contributors, formatter, finalSeparator = '&') => {
  const names = contributors.map(formatter).filter(Boolean)

  if (names.length <= 1) return names[0] || ''
  if (names.length === 2) return `${names[0]} ${finalSeparator} ${names[1]}`

  return `${names.slice(0, -1).join(', ')}, ${finalSeparator} ${names.at(-1)}`
}

const formatApaNames = (contributors) => {
  const names = contributors.map(formatInvertedName).filter(Boolean)

  if (names.length === 0) return ''
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]}, & ${names[1]}`
  if (names.length <= 20) return `${names.slice(0, -1).join(', ')}, & ${names.at(-1)}`

  return `${names.slice(0, 19).join(', ')}, … ${names.at(-1)}`
}

const formatApaInitialsFirstNames = (contributors) => {
  const names = contributors.map(formatInitialsFirstName).filter(Boolean)

  if (names.length === 0) return ''
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} & ${names[1]}`

  return `${names.slice(0, -1).join(', ')}, & ${names.at(-1)}`
}

// MLA/Chicago: first contributor inverted, remaining ones in natural order.
const formatBookStyleNames = (contributors, locale) => {
  const names = contributors.map(formatFullName).filter(Boolean)

  if (names.length === 0) return ''

  const firstName = formatInvertedFullName(contributors[0])
  const conjunction = isPolish(locale) ? 'i' : 'and'

  if (names.length === 1) return firstName
  if (names.length === 2) return `${firstName}, ${conjunction} ${names[1]}`

  return `${firstName}, ${names.slice(1, -1).join(', ')}, ${conjunction} ${names.at(-1)}`
}

const formatNumberedStyleNames = (contributors) => {
  const names = contributors.map(formatVancouverName).filter(Boolean)

  if (names.length === 0) return ''
  if (names.length <= 6) return names.join(', ')

  return `${names.slice(0, 6).join(', ')}, et al`
}

const getEditorLabel = (count, locale) => {
  if (isPolish(locale)) return 'red.'

  return count === 1 ? 'Ed.' : 'Eds.'
}

const getPagesPrefix = (pages, locale) => {
  if (!pages) return ''

  if (isPolish(locale)) return 's.'

  return /[-–—]/.test(pages) ? 'pp.' : 'p.'
}

const getInLabel = (locale) => (isPolish(locale) ? 'W:' : 'In')

const quoteTitle = (title) => {
  const text = clean(title)

  return text ? `"${terminate(text)}"` : ''
}

// MLA/Chicago: „pod red. Jan Kowalski" / „edited by Jan Kowalski"
const formatEditedByPhrase = (editors, locale) => {
  if (editors.length === 0) return ''

  const names = formatNames(editors, formatFullName, isPolish(locale) ? 'i' : 'and')

  return isPolish(locale) ? `red. ${names}` : `edited by ${names}`
}

const getNumberedEditorLabel = (count, locale) => {
  if (isPolish(locale)) return 'red.'

  return count === 1 ? 'ed' : 'eds'
}

const formatYear = (year, locale) => {
  const value = clean(year)

  if (value) return value

  return isPolish(locale) ? 'b.d.' : 'n.d.'
}

const getDoiUrl = (doi) => {
  if (!doi) return ''

  return doi.startsWith('http://') || doi.startsWith('https://') ? doi : `https://doi.org/${doi}`
}

const getPublishersText = (source) => source.publishers.join('; ')

const getEnglishOrdinal = (number) => {
  const lastTwoDigits = number % 100

  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) return `${number}th`

  return `${number}${{ 1: 'st', 2: 'nd', 3: 'rd' }[number % 10] || 'th'}`
}

// Style cytowania nie podają pierwszego wydania; numer kolejnego zapisujemy jako „wyd. 2” / „2nd ed.”.
const formatEdition = (edition, locale) => {
  if (!/^\d+$/.test(edition)) return edition

  const number = Number(edition)

  if (number <= 1) return ''

  return isPolish(locale) ? `wyd. ${number}` : `${getEnglishOrdinal(number)} ed.`
}

/**
 * Normalized citation source shared by every publication type.
 *
 * @typedef {object} CitationSource
 * @property {'article'|'chapter'|'book'} type
 * @property {object[]} authors
 * @property {object[]} editors container editors (book/chapter)
 * @property {string} title
 * @property {string} containerTitle journal title (article) or book title (chapter)
 * @property {string[]} publishers
 * @property {string} volume
 * @property {string} issue
 * @property {string} pages
 * @property {string} edition
 * @property {string|number|null} year
 * @property {string} doi
 * @property {string[]} isbns
 */

const normalizeSource = (source) => ({
  type: source?.type || 'article',
  authors: (source?.authors || []).filter((contributor) => formatFullName(contributor)),
  editors: (source?.editors || []).filter((contributor) => formatFullName(contributor)),
  title: clean(source?.title),
  containerTitle: clean(source?.containerTitle),
  publishers: (source?.publishers || []).map(clean).filter(Boolean),
  volume: clean(source?.volume),
  issue: clean(source?.issue),
  pages: clean(source?.pages),
  edition: clean(source?.edition),
  year: source?.year || null,
  doi: clean(source?.doi),
  isbns: (source?.isbns || []).map(clean).filter(Boolean),
})

const getCreators = (source) => (source.authors.length > 0 ? source.authors : source.editors)

const isEditorCreator = (source) => source.authors.length === 0 && source.editors.length > 0

/* ------------------------------------------------------------------ APA 7 */

const apaTitleBlock = (source) => {
  if (source.type === 'book') {
    return terminate(join([italic(source.title), source.edition ? `(${source.edition})` : ''], ' '))
  }

  return terminate(source.title)
}

const apaContainerBlock = (source, locale) => {
  if (source.type === 'article') {
    // Numer bez tomu też trafia do opisu — APA podaje go wtedy w nawiasie zaraz po tytule czasopisma.
    const volumeIssue = source.volume
      ? `${italic(source.volume)}${source.issue ? `(${source.issue})` : ''}`
      : source.issue
        ? `(${source.issue})`
        : ''

    return terminate(join([italic(source.containerTitle), volumeIssue, source.pages], ', '))
  }

  if (source.type === 'chapter') {
    if (!source.containerTitle) return ''

    const editors = source.editors.length
      ? `${formatApaInitialsFirstNames(source.editors)} (${getEditorLabel(source.editors.length, locale)}),`
      : ''
    const details = join(
      [
        source.edition ? `(${source.edition})` : '',
        source.pages ? `(${getPagesPrefix(source.pages, locale)} ${source.pages})` : '',
      ],
      ' ',
    )

    return terminate(join([getInLabel(locale), editors, join([italic(source.containerTitle), details], ' ')], ' '))
  }

  return ''
}

const buildApa = (source, locale) => {
  const creators = getCreators(source)
  const creatorsText = creators.length
    ? terminate(
        join(
          [formatApaNames(creators), isEditorCreator(source) ? `(${getEditorLabel(creators.length, locale)})` : ''],
          ' ',
        ),
      )
    : ''
  const year = `(${formatYear(source.year, locale)}).`
  const publishers = source.type === 'article' ? '' : terminate(getPublishersText(source))
  const doiUrl = getDoiUrl(source.doi)

  const head = creatorsText ? [creatorsText, year, apaTitleBlock(source)] : [apaTitleBlock(source), year]

  return join([...head, apaContainerBlock(source, locale), publishers, doiUrl], ' ')
}

/* ------------------------------------------------------------------- MLA 9 */

const buildMla = (source, locale) => {
  const creators = getCreators(source)
  const role = isEditorCreator(source)
    ? isPolish(locale)
      ? ' (red.)'
      : creators.length === 1
        ? ', editor'
        : ', editors'
    : ''
  const creatorsText = creators.length ? terminate(`${formatBookStyleNames(creators, locale)}${role}`) : ''
  const year = formatYear(source.year, locale)
  const doiUrl = getDoiUrl(source.doi)

  if (source.type === 'book') {
    const container = terminate(join([italic(source.title), source.edition ? `(${source.edition})` : ''], ' '))

    return join([creatorsText, container, terminate(join([getPublishersText(source), year], ', ')), doiUrl], ' ')
  }

  const title = quoteTitle(source.title)

  if (source.type === 'chapter') {
    const pages = source.pages ? `${getPagesPrefix(source.pages, locale)} ${source.pages}` : ''
    const container = terminate(
      join(
        [
          italic(source.containerTitle),
          formatEditedByPhrase(source.editors, locale),
          getPublishersText(source),
          year,
          pages,
        ],
        ', ',
      ),
    )

    return join([creatorsText, title, container, doiUrl], ' ')
  }

  const volume = source.volume ? `vol. ${source.volume}` : ''
  const issue = source.issue ? `no. ${source.issue}` : ''
  const pages = source.pages ? `${getPagesPrefix(source.pages, locale)} ${source.pages}` : ''
  const container = terminate(join([italic(source.containerTitle), volume, issue, year, pages], ', '))

  return join([creatorsText, title, container, doiUrl], ' ')
}

/* ---------------------------------------------------------------- Chicago */

const buildChicago = (source, locale) => {
  const creators = getCreators(source)
  const role = isEditorCreator(source)
    ? isPolish(locale)
      ? ' (red.)'
      : creators.length === 1
        ? ', ed.'
        : ', eds.'
    : ''
  const creatorsText = creators.length ? terminate(`${formatBookStyleNames(creators, locale)}${role}`) : ''
  const year = formatYear(source.year, locale)
  const doiUrl = getDoiUrl(source.doi)

  if (source.type === 'book') {
    const title = terminate(join([italic(source.title), source.edition ? `(${source.edition})` : ''], ' '))

    return join([creatorsText, title, terminate(join([getPublishersText(source), year], ', ')), terminate(doiUrl)], ' ')
  }

  const title = quoteTitle(source.title)

  if (source.type === 'chapter') {
    const container = terminate(
      join(
        [
          source.containerTitle ? `${getInLabel(locale)} ${italic(source.containerTitle)}` : '',
          formatEditedByPhrase(source.editors, locale),
          source.pages,
        ],
        ', ',
      ),
    )

    return join(
      [creatorsText, title, container, terminate(join([getPublishersText(source), year], ', ')), terminate(doiUrl)],
      ' ',
    )
  }

  const volumeIssue = join([source.volume, source.issue ? `no. ${source.issue}` : ''], ', ')
  const container = join([italic(source.containerTitle), volumeIssue], ' ')
  const yearPages = source.pages ? `(${year}): ${source.pages}` : `(${year})`

  return join([creatorsText, title, terminate(join([container, yearPages], ' ')), terminate(doiUrl)], ' ')
}

/* ---------------------------------------------------------------- Harvard */

const buildHarvard = (source, locale) => {
  const creators = getCreators(source)
  const role = isEditorCreator(source) ? (creators.length === 1 ? ' (ed.)' : ' (eds.)') : ''
  const creatorsText = creators.length ? `${formatNames(creators, formatInvertedName, '&')}${role}` : ''
  const year = formatYear(source.year, locale)
  const doi = source.doi ? `doi: ${source.doi}` : ''

  if (source.type === 'book') {
    return terminate(
      join(
        [
          join([creatorsText, year], ' '),
          join([italic(source.title), source.edition ? `(${source.edition})` : ''], ' '),
          getPublishersText(source),
          doi,
        ],
        ', ',
      ),
    )
  }

  if (source.type === 'chapter') {
    const editors = source.editors.length
      ? `${formatNames(source.editors, formatInvertedName, '&')} (${getEditorLabel(source.editors.length, locale)})`
      : ''
    const pages = source.pages ? `${getPagesPrefix(source.pages, locale)} ${source.pages}` : ''
    const container =
      source.containerTitle || editors
        ? join([getInLabel(locale).replace(':', ''), editors ? `${editors},` : '', italic(source.containerTitle)], ' ')
        : ''

    return terminate(
      join(
        [
          join([creatorsText, year], ' '),
          source.title ? `'${source.title}'` : '',
          container,
          getPublishersText(source),
          pages,
          doi,
        ],
        ', ',
      ),
    )
  }

  const volume = source.volume ? `vol. ${source.volume}` : ''
  const issue = source.issue ? `no. ${source.issue}` : ''
  const pages = source.pages ? `${getPagesPrefix(source.pages, locale)} ${source.pages}` : ''

  return terminate(
    join(
      [
        join([creatorsText, year], ' '),
        source.title ? `'${source.title}'` : '',
        italic(source.containerTitle),
        volume,
        issue,
        pages,
        doi,
      ],
      ', ',
    ),
  )
}

/* ----------------------------------------------------------- Vancouver/AMA */

const buildNumberedStyle = (source, locale, { italicContainer, doiPrefix }) => {
  const creators = getCreators(source)
  const role = isEditorCreator(source) ? `, ${getNumberedEditorLabel(creators.length, locale)}` : ''
  const creatorsText = creators.length ? terminate(`${formatNumberedStyleNames(creators)}${role}`) : ''
  const year = formatYear(source.year, locale)
  const doi = source.doi ? `${doiPrefix}${source.doi}` : ''
  const containerTitle = italicContainer ? italic(source.containerTitle) : source.containerTitle

  if (source.type === 'article') {
    const issueText = source.issue ? `(${source.issue})` : ''
    const pagesText = source.pages ? `:${source.pages}` : ''
    const volumeText = source.volume ? `;${source.volume}` : ''
    const locator = `${year}${volumeText}${issueText}${pagesText}`

    return join(
      [creatorsText, terminate(source.title), terminate(containerTitle), terminate(locator), terminate(doi)],
      ' ',
    )
  }

  if (source.type === 'chapter') {
    const inLabel = `${getInLabel(locale).replace(':', '')}:`
    const editors = source.editors.length
      ? terminate(
          `${inLabel} ${formatNumberedStyleNames(source.editors)}, ${getNumberedEditorLabel(source.editors.length, locale)}`,
        )
      : source.containerTitle
        ? inLabel
        : ''
    const locator = terminate(
      join([getPublishersText(source), `${year}${source.pages ? `:${source.pages}` : ''}`], '; '),
    )

    return join(
      [creatorsText, terminate(source.title), editors, terminate(containerTitle), locator, terminate(doi)],
      ' ',
    )
  }

  const title = terminate(join([italic(source.title), source.edition ? `(${source.edition})` : ''], ' '))
  const locator = terminate(join([getPublishersText(source), year], '; '))

  return join([creatorsText, title, locator, terminate(doi)], ' ')
}

const STYLE_BUILDERS = {
  apa: buildApa,
  mla: buildMla,
  chicago: buildChicago,
  harvard: buildHarvard,
  vancouver: (source, locale) => buildNumberedStyle(source, locale, { italicContainer: false, doiPrefix: 'doi: ' }),
  ama: (source, locale) => buildNumberedStyle(source, locale, { italicContainer: true, doiPrefix: 'doi:' }),
}

export const buildCitation = (source, style, locale) => {
  const normalized = normalizeSource(source)
  const builder = STYLE_BUILDERS[style] || STYLE_BUILDERS.apa

  return builder({ ...normalized, edition: formatEdition(normalized.edition, locale) }, locale)
    .replace(/\s+/g, ' ')
    .trim()
}

/* ------------------------------------------------------------------ BibTeX */

const BIBTEX_TYPES = {
  article: 'article',
  chapter: 'incollection',
  book: 'book',
}

const getCiteKey = (source) => {
  const creators = getCreators(source)
  const { lastName } = getNameParts(creators[0] || {})
  const normalizedName = lastName.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '')

  return `${normalizedName || source.type}${source.year || 'nd'}`
}

export const buildBibTeX = (source) => {
  const normalized = normalizeSource(source)
  const entryType = BIBTEX_TYPES[normalized.type] || 'misc'
  const containerField = normalized.type === 'article' ? 'journal' : 'booktitle'
  const fields = [
    normalized.authors.length > 0 ? ['author', normalized.authors.map(formatFullName).join(' and ')] : null,
    normalized.editors.length > 0 ? ['editor', normalized.editors.map(formatFullName).join(' and ')] : null,
    normalized.title ? ['title', titleToText(normalized.title)] : null,
    normalized.type !== 'book' && normalized.containerTitle
      ? [containerField, titleToText(normalized.containerTitle)]
      : null,
    normalized.year ? ['year', normalized.year] : null,
    normalized.volume ? ['volume', normalized.volume] : null,
    normalized.issue ? ['number', normalized.issue] : null,
    normalized.pages ? ['pages', normalized.pages] : null,
    normalized.publishers.length > 0 ? ['publisher', normalized.publishers.join(' and ')] : null,
    normalized.edition ? ['edition', normalized.edition] : null,
    normalized.isbns.length > 0 ? ['isbn', normalized.isbns.join(', ')] : null,
    normalized.doi ? ['doi', normalized.doi] : null,
  ]
    .filter(Boolean)
    .map(([name, value]) => `  ${name} = {${value}}`)

  return `@${entryType}{${getCiteKey(normalized)},\n${fields.join(',\n')}\n}`
}

/* --------------------------------------------------------------------- RIS */

const RIS_TYPES = {
  article: 'JOUR',
  chapter: 'CHAP',
  book: 'BOOK',
}

export const buildRIS = (source) => {
  const normalized = normalizeSource(source)
  const lines = [`TY  - ${RIS_TYPES[normalized.type] || 'GEN'}`]

  normalized.authors.forEach((author) => lines.push(`AU  - ${formatInvertedFullName(author)}`))
  normalized.editors.forEach((editor) => lines.push(`ED  - ${formatInvertedFullName(editor)}`))
  if (normalized.title) lines.push(`TI  - ${titleToText(normalized.title)}`)
  if (normalized.type !== 'book' && normalized.containerTitle) {
    lines.push(`${normalized.type === 'article' ? 'JO' : 'T2'}  - ${titleToText(normalized.containerTitle)}`)
  }
  if (normalized.year) lines.push(`PY  - ${normalized.year}`)
  if (normalized.volume) lines.push(`VL  - ${normalized.volume}`)
  if (normalized.issue) lines.push(`IS  - ${normalized.issue}`)
  if (normalized.pages) {
    const [pageFrom, pageTo] = normalized.pages.split(/\s*[-–—]\s*/)
    if (pageFrom) lines.push(`SP  - ${pageFrom}`)
    if (pageTo) lines.push(`EP  - ${pageTo}`)
  }
  normalized.publishers.forEach((publisher) => lines.push(`PB  - ${publisher}`))
  if (normalized.edition) lines.push(`ET  - ${normalized.edition}`)
  normalized.isbns.forEach((isbn) => lines.push(`SN  - ${isbn}`))
  if (normalized.doi) lines.push(`DO  - ${normalized.doi}`)
  lines.push('ER  - ')

  return `${lines.join('\n')}\n`
}

/* --------------------------------------------------------- source builders */

const getTranslatedPublisherNames = (entity, locale) => {
  const publishers = (entity?.publishers || [])
    .map((publisher) => getPreferredTranslation(publisher, locale)?.title || publisher?.title || '')
    .filter(Boolean)

  if (publishers.length > 0) return publishers

  const translatedTitles = entity?.publishersTitles || {}
  const localeKey = String(locale || '').toLowerCase()

  return (
    translatedTitles[locale] || translatedTitles[localeKey] || Object.values(translatedTitles).find(Array.isArray) || []
  )
}

const getContributors = (contributors) =>
  (Array.isArray(contributors) ? contributors : []).filter(
    (contributor) => contributor?.fullName || contributor?.lastName,
  )

const getPageRange = (publication) => {
  const pageFrom = clean(publication?.pageFrom)
  const pageTo = clean(publication?.pageTo)

  if (pageFrom && pageTo) return `${pageFrom}-${pageTo}`

  return pageFrom || pageTo || ''
}

export const buildArticleSource = (article, locale) => {
  const translation = getPreferredTranslation(article, locale) || {}
  const journalTranslation = article?.journal ? getPreferredTranslation(article.journal, locale) || {} : {}

  return {
    type: 'article',
    authors: getContributors(article?.authors),
    editors: [],
    title: titleToHtml(translation?.title),
    containerTitle: journalTranslation?.title || '',
    publishers: [],
    volume: getPreferredTranslation(article?.newestVolume, locale)?.title || '',
    issue: article?.issue?.number || '',
    // Zakres stron artykułu jest w pageFrom/pageTo; `pages` to stare pole z importów.
    pages: clean(article?.pages) || getPageRange(article),
    edition: '',
    year: article?.issue?.releaseYear || article?.publicationDate?.slice(0, 4) || null,
    doi: article?.doi || '',
    isbns: [],
  }
}

export const buildChapterSource = (chapter, locale) => {
  const translationOptions = {
    fallbackLocale: null,
    includeDefaultLang: false,
    includeFirstTranslation: false,
  }
  const translation = getPreferredTranslation(chapter, locale, translationOptions) || {}
  const monograph = chapter?.monograph || {}
  const monographTranslation = getPreferredTranslation(monograph, locale, translationOptions) || {}

  return {
    type: 'chapter',
    authors: getContributors(chapter?.authors),
    editors: getContributors(monograph?.scientificEditors),
    title: titleToHtml(translation?.title),
    containerTitle: titleToHtml(monographTranslation?.title),
    publishers: getTranslatedPublisherNames(monograph, locale),
    volume: '',
    issue: '',
    pages: getPageRange(chapter),
    edition: monographTranslation?.edition?.trim() || '',
    year: monograph?.releaseYear || null,
    doi: chapter?.doi || '',
    isbns: [],
  }
}

export const buildBookSource = (book, locale) => {
  const translation = getPreferredTranslation(book, locale) || {}

  return {
    type: 'book',
    authors: getContributors(book?.authors),
    editors: getContributors(book?.scientificEditors),
    title: titleToHtml(translation?.title),
    containerTitle: '',
    publishers: getTranslatedPublisherNames(book, locale),
    volume: '',
    issue: '',
    pages: '',
    edition: translation?.edition?.trim() || '',
    year: book?.releaseYear || null,
    doi: book?.doi || '',
    isbns: [book?.isbn, book?.eIsbn].map(clean).filter(Boolean),
  }
}

const createGenerators = (buildSource) => ({
  citation: (entity, style, locale) => buildCitation(buildSource(entity, locale), style, locale),
  bibtex: (entity, locale) => buildBibTeX(buildSource(entity, locale)),
  ris: (entity, locale) => buildRIS(buildSource(entity, locale)),
})

export const articleCitationGenerators = createGenerators(buildArticleSource)
export const chapterCitationGenerators = createGenerators(buildChapterSource)
export const bookCitationGenerators = createGenerators(buildBookSource)
